import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  Tile,
  Stack,
  ToastNotification,
  Form,
  FormGroup,
  Checkbox,
  RadioButtonGroup,
  RadioButton,
  Dropdown,
} from "@carbon/react";
import { callApi } from "../../../services/apiService";

export default function Schedule({
  propertyUuid = "",
  onComplete,
  onBack,
  CommonButtons,
  onNavigateNext,
  currentStep = 7,
  totalSteps = 7,
  onSaveSuccess,
  saveSectionAttributes,
  formMode = "add",
  lastSavedStep = 0,
}) {
  const [form, setForm] = useState({
    contactId: "",
    propertyUuid: propertyUuid || "",
    availability: "",
    startTime: "",
    endTime: "",
    availableAllDay: false,
  });

  const [touched, setTouched] = useState({});

  // Time states for start time
  const [startHour, setStartHour] = useState("09");
  const [startMinute, setStartMinute] = useState("00");
  const [startPeriod, setStartPeriod] = useState("AM");

  // Time states for end time
  const [endHour, setEndHour] = useState("06");
  const [endMinute, setEndMinute] = useState("00");
  const [endPeriod, setEndPeriod] = useState("PM");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [, setIsInitialized] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Time options
  const hourOptions = useMemo(() => {
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    return hours.map(hour => ({
      id: String(hour).padStart(2, '0'),
      text: String(hour).padStart(2, '0'),
      label: String(hour).padStart(2, '0')
    }));
  }, []);

const minuteOptions = useMemo(() => {
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  return minutes.map(minute => ({
    id: minute,
    text: minute,
    label: minute
  }));
}, []);

  const periodOptions = useMemo(() => [
    { id: "AM", text: "AM", label: "AM" },
    { id: "PM", text: "PM", label: "PM" }
  ], []);

  // Update start time
  const updateStartTime = useCallback((type, value) => {
    if (type === "hour") {
      setStartHour(value);
    } else if (type === "minute") {
      setStartMinute(value);
    } else if (type === "period") {
      setStartPeriod(value);
    }
    
    const newHour = type === "hour" ? value : startHour;
    const newMinute = type === "minute" ? value : startMinute;
    const newPeriod = type === "period" ? value : startPeriod;
    
    const timeString = `${newHour}:${newMinute} ${newPeriod}`;
    setForm(prev => ({ ...prev, startTime: timeString }));
  }, [startHour, startMinute, startPeriod]);

  // Update end time
  const updateEndTime = useCallback((type, value) => {
    if (type === "hour") {
      setEndHour(value);
    } else if (type === "minute") {
      setEndMinute(value);
    } else if (type === "period") {
      setEndPeriod(value);
    }
    
    const newHour = type === "hour" ? value : endHour;
    const newMinute = type === "minute" ? value : endMinute;
    const newPeriod = type === "period" ? value : endPeriod;
    
    const timeString = `${newHour}:${newMinute} ${newPeriod}`;
    setForm(prev => ({ ...prev, endTime: timeString }));
  }, [endHour, endMinute, endPeriod]);

  // Availability options
  const availabilityOptions = [
    { id: "everyday", label: "Everyday (Mon-Sun)", value: "everyday" },
    { id: "weekday", label: "Weekday (Mon-Fri)", value: "weekday" },
    { id: "weekend", label: "Weekend (Sat-Sun)", value: "weekend" }
  ];

  // Reset to default values
  const resetToDefaults = useCallback(() => {
    setStartHour("09");
    setStartMinute("00");
    setStartPeriod("AM");
    setEndHour("06");
    setEndMinute("00");
    setEndPeriod("PM");
    
    const startTimeStr = "09:00 AM";
    const endTimeStr = "06:00 PM";
    
    setForm(prev => ({
      ...prev,
      contactId: "",
      availability: "",
      startTime: startTimeStr,
      endTime: endTimeStr,
      availableAllDay: false,
    }));
  }, []);

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  // Function to check if propertyUuid is valid
  const isValidPropertyUuid = useCallback((uuid) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuid && uuid.trim() !== "" && uuidRegex.test(uuid);
  }, []);

  // Parse ISO time to 12h format components
  const parseISOTimeTo12h = useCallback((isoTime) => {
    if (!isoTime) {
      return { hour: "09", minute: "00", period: "AM" };
    }
    
    try {
      const date = new Date(isoTime);
      
      if (isNaN(date.getTime())) {
        return { hour: "09", minute: "00", period: "AM" };
      }
      
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? "PM" : "AM";
      
      hours = hours % 12 || 12;
      
      return {
        hour: String(hours).padStart(2, "0"),
        minute: String(minutes).padStart(2, "0"),
        period: period
      };
    } catch (error) {
      return { hour: "09", minute: "00", period: "AM" };
    }
  }, []);

  // Helper function to parse available all day value from API
  const parseAvailableAllDay = useCallback((value) => {
    if (!value) return false;
    
    const stringValue = String(value).toUpperCase();
    
    // Check for various truthy values including truncated "Ye" from API
    return stringValue === "YES" || 
           stringValue === "Y" || 
           stringValue === "TRUE" ||
           stringValue === "1" ||
           stringValue === "YE";
  }, []);

  // Fetch existing appointment data
  const fetchAppointment = useCallback(async (uuid) => {
    const currentUuid = uuid || propertyUuid;
    
    if (!isValidPropertyUuid(currentUuid)) {
      resetToDefaults();
      setIsInitialized(true);
      setHasFetched(false);
      return;
    }
    
    if (hasFetched && currentUuid === propertyUuid) {
      return;
    }
    
    try {
      setLoading(true);
      
      const resp = await callApi(
        "GET",
        `/property-contacts/?property_uuid=${currentUuid}`
      );
      
      if (resp?.status_code === 200 && resp?.data?.length > 0) {
        const d = resp.data[0];

        const isAvailableAllDay = parseAvailableAllDay(d.appt_all_day);
        
        let startTimeComponents = { hour: "09", minute: "00", period: "AM" };
        let endTimeComponents = { hour: "06", minute: "00", period: "PM" };
        
        if (!isAvailableAllDay && d.appt_start_time && d.appt_end_time) {
          startTimeComponents = parseISOTimeTo12h(d.appt_start_time);
          endTimeComponents = parseISOTimeTo12h(d.appt_end_time);
        }
        
        setStartHour(startTimeComponents.hour);
        setStartMinute(startTimeComponents.minute);
        setStartPeriod(startTimeComponents.period);
        
        setEndHour(endTimeComponents.hour);
        setEndMinute(endTimeComponents.minute);
        setEndPeriod(endTimeComponents.period);

        const startTimeStr = isAvailableAllDay ? "" : `${startTimeComponents.hour}:${startTimeComponents.minute} ${startTimeComponents.period}`;
        const endTimeStr = isAvailableAllDay ? "" : `${endTimeComponents.hour}:${endTimeComponents.minute} ${endTimeComponents.period}`;

        let availabilityValue = d.appt_availability?.toLowerCase() || "";
        
        if (!["everyday", "weekday", "weekend"].includes(availabilityValue)) {
          availabilityValue = "";
        }

        setForm({
          contactId: d.contact_id ? String(d.contact_id) : "",
          propertyUuid: currentUuid,
          availability: availabilityValue,
          startTime: startTimeStr,
          endTime: endTimeStr,
          availableAllDay: isAvailableAllDay,
        });
        
        setHasFetched(true);
      } else {
        resetToDefaults();
        setHasFetched(true);
      }
    } catch (err) {
      if (isValidPropertyUuid(currentUuid)) {
        setError("Failed to fetch existing appointment details.");
      }
      resetToDefaults();
      setHasFetched(false);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [propertyUuid, resetToDefaults, isValidPropertyUuid, hasFetched, parseISOTimeTo12h, parseAvailableAllDay]);

  // Handle initial load and propertyUuid changes
  useEffect(() => {
    if (isValidPropertyUuid(propertyUuid)) {
      if (form.propertyUuid !== propertyUuid) {
        setHasFetched(false);
      }
      
      if (!hasFetched || form.propertyUuid !== propertyUuid) {
        fetchAppointment(propertyUuid);
      }
    } else {
      resetToDefaults();
      setIsInitialized(true);
      setHasFetched(false);
    }
    
    if (propertyUuid) {
      setForm(prev => ({ ...prev, propertyUuid }));
    }
  }, [propertyUuid, fetchAppointment, resetToDefaults, isValidPropertyUuid, hasFetched, form.propertyUuid]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAvailableAllDayChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      availableAllDay: checked,
      startTime: checked ? "" : prev.startTime,
      endTime: checked ? "" : prev.endTime,
    }));
    
    if (checked) {
      setStartHour("09");
      setStartMinute("00");
      setStartPeriod("AM");
      setEndHour("06");
      setEndMinute("00");
      setEndPeriod("PM");
    }
  };

  // Convert 12h → ISO format for API
  const time12toISO = (timeStr) => {
    if (!timeStr || !timeStr.trim()) return null;

    try {
      const [time, meridiem] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (meridiem === "PM" && hours !== 12) hours += 12;
      if (meridiem === "AM" && hours === 12) hours = 0;

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    } catch (error) {
      return null;
    }
  };

  // Format availability for API (uppercase)
  const formatAvailabilityForAPI = (availability) => {
    switch (availability) {
      case "weekday":
        return "WEEKDAY";
      case "weekend":
        return "WEEKEND";
      case "everyday":
        return "EVERYDAY";
      default:
        return null;
    }
  };

const validate = () => {
  setTouched({
    availability: true,
  });

  if (!form.availability) {
    setError("Please fill all required fields");
    return false;
  }

  return true;
};

  // Save appointment using the POST endpoint
  const saveAppointment = async () => {
    if (!isValidPropertyUuid(propertyUuid)) {
      setError("Property UUID is required to save appointment");
      return { ok: false };
    }
    
const isValid = validate();

if (!isValid) {
  return { ok: false };
}
    setSaving(true);
    setError("");

    try {
      let startTimeString = null;
      let endTimeString = null;
      let availableAllDayValue = "N";
      
      if (form.availableAllDay) {
        availableAllDayValue = "Y";
        startTimeString = null;
        endTimeString = null;
      } else {
        startTimeString = `${startHour}:${startMinute} ${startPeriod}`;
        endTimeString = `${endHour}:${endMinute} ${endPeriod}`;
        availableAllDayValue = "N";
      }
      
      const formattedAvailability = formatAvailabilityForAPI(form.availability);
      
      const payload = {
        property_uuid: propertyUuid,
        APPT_AVAILABILITY: formattedAvailability,
        APPT_AVAILBLE_START_TIME: startTimeString ? time12toISO(startTimeString) : null,
        APPT_AVAIALBLE_END_TIME: endTimeString ? time12toISO(endTimeString) : null,
        APPT_AVAILABLE_ALL_DAY: availableAllDayValue,
      };

      const res = await callApi("POST", "/property-contacts/appointment", payload);

      if (res?.status_code === 200) {
        setSuccess("Schedule details saved successfully!");
        
        if (res.data) {
          const d = res.data;
          const responseAvailableAllDay = parseAvailableAllDay(d.APPT_AVAILABLE_ALL_DAY);
          
          let startTimeComponents = { hour: "09", minute: "00", period: "AM" };
          let endTimeComponents = { hour: "06", minute: "00", period: "PM" };
          
          if (!responseAvailableAllDay && d.APPT_AVAILBLE_START_TIME && d.APPT_AVAIALBLE_END_TIME) {
            startTimeComponents = parseISOTimeTo12h(d.APPT_AVAILBLE_START_TIME);
            endTimeComponents = parseISOTimeTo12h(d.APPT_AVAIALBLE_END_TIME);
          }
          
          setStartHour(startTimeComponents.hour);
          setStartMinute(startTimeComponents.minute);
          setStartPeriod(startTimeComponents.period);
          
          setEndHour(endTimeComponents.hour);
          setEndMinute(endTimeComponents.minute);
          setEndPeriod(endTimeComponents.period);

          let availabilityValue = (d.APPT_AVAILABILITY || d.appt_availability)?.toLowerCase() || "";
          if (!["everyday", "weekday", "weekend"].includes(availabilityValue)) {
            availabilityValue = "";
          }

          setForm(prev => ({
            ...prev,
            contactId: d.PROPERTY_CONTACTS_ID || d.contact_id ? String(d.PROPERTY_CONTACTS_ID || d.contact_id) : "",
            availability: availabilityValue,
            availableAllDay: responseAvailableAllDay,
            startTime: responseAvailableAllDay ? "" : `${startTimeComponents.hour}:${startTimeComponents.minute} ${startTimeComponents.period}`,
            endTime: responseAvailableAllDay ? "" : `${endTimeComponents.hour}:${endTimeComponents.minute} ${endTimeComponents.period}`,
          }));
        }
        
        setHasFetched(false);
        onSaveSuccess?.(propertyUuid, form, currentStep);
        
        return { ok: true };
      } else {
        throw new Error(res?.message || "Failed to save appointment");
      }
    } catch (err) {
      setError(err?.message || "Failed to save appointment. Please try again.");
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async (e) => {
    e?.preventDefault();
    const result = await saveAppointment();
    if (result.ok) {
      setTimeout(() => {
        onComplete?.(form, true);
      }, 500);
    }
  };

  const handleNext = async (e) => {
    e?.preventDefault?.();
    const result = await saveAppointment();
    if (result.ok && onNavigateNext) {
      setTimeout(() => {
        onNavigateNext(form);
      }, 500);
    }
  };

  const handleSaveOnly = async () => {
    await saveAppointment();
  };

  // Time Picker Component
  const TimePickerComponent = ({ 
    label, 
    hour, 
    minute, 
    period, 
    onHourChange, 
    onMinuteChange, 
    onPeriodChange,
    disabled 
  }) => (
    <FormGroup legendText={label}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginTop: "6px",
        flexWrap: "wrap",
      }}>
        {/* Hour Dropdown */}
        <div style={{ minWidth: "110px" }}>
          <Dropdown
            id={`${label.toLowerCase().replace(' ', '-')}-hour`}
            label="Hour"
            hideLabel
            items={hourOptions}
            itemToString={(item) => item?.text || ""}
            selectedItem={hourOptions.find((h) => h.id === hour) || hourOptions[0]}
            onChange={({ selectedItem }) => onHourChange(selectedItem?.id || "09")}
            disabled={disabled}
          />
        </div>
        <span style={{
          fontSize: "1.2rem",
          color: "#525252",
          marginBottom: "6px",
        }}>
          :
        </span>
        {/* Minute Dropdown */}
        <div style={{ minWidth: "110px" }}>
          <Dropdown
            id={`${label.toLowerCase().replace(' ', '-')}-minute`}
            label="Minute"
            hideLabel
            items={minuteOptions}
            itemToString={(item) => item?.text || ""}
            selectedItem={
              minuteOptions.find((m) => m.id === minute) || minuteOptions[0]
            }
            onChange={({ selectedItem }) =>
              onMinuteChange(selectedItem?.id || "00")
            }
            disabled={disabled}
          />
        </div>
        {/* AM/PM Dropdown */}
        <div style={{ minWidth: "110px" }}>
          <Dropdown
            id={`${label.toLowerCase().replace(' ', '-')}-period`}
            label="AM/PM"
            hideLabel
            items={periodOptions}
            itemToString={(item) => item?.text || ""}
            selectedItem={
              periodOptions.find((p) => p.id === period) || periodOptions[0]
            }
            onChange={({ selectedItem }) =>
              onPeriodChange(selectedItem?.id || "AM")
            }
            disabled={disabled}
          />
        </div>
      </div>
    </FormGroup>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        Loading appointment details...
      </div>
    );
  }

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={4000}
          onClose={() => setError("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

     
     
      <div style={{ backgroundColor: "white" }}>
        <Grid fullWidth style={{ padding: "0 1rem" }}>
          <Column  md={4} sm ={4} lg={16}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>


  <p
    style={{
      color: "#0f62fe",
      fontSize: "0.95rem",
      fontWeight: 500,
      maxWidth: "700px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
    Make house visits hassle-free by providing your availability for property visits.
  </p>
</div>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
              <Form onSubmit={handleFinish}>
                <Stack gap={6}>
                  {/* Availability Field */}
                  <FormGroup>
                   <RadioButtonGroup
                      name="availability"
                      legendText={requiredLabel("Availability ")}
                      valueSelected={form.availability}
                      onChange={(value) => {
                        updateField("availability", value);

                        setTouched((prev) => ({
                          ...prev,
                          availability: true,
                        }));
                      }}
                      invalid={touched.availability && !form.availability}
                      invalidText={
                        touched.availability && !form.availability
                          ? "Availability is required"
                          : ""
                      }
                      orientation="vertical"
                      disabled={!isValidPropertyUuid(propertyUuid) || saving}
                    >
                      {availabilityOptions.map((option) => (
                        <RadioButton
                          key={option.id}
                          id={option.id}
                          labelText={option.label}
                          value={option.value}
                          checked={form.availability === option.value}
                          disabled={!isValidPropertyUuid(propertyUuid) || saving}
                        />
                      ))}
                    </RadioButtonGroup>
                  </FormGroup>

                  <FormGroup legendText="Time Window">
                    <Checkbox
                      id="availableAllDay"
                      labelText="Available All Time"
                      checked={form.availableAllDay}
                      onChange={(_, { checked }) =>
                        handleAvailableAllDayChange(checked)
                      }
                      disabled={!isValidPropertyUuid(propertyUuid) || saving}
                    />

                    {!form.availableAllDay && (
                      <div style={{ marginTop: "1rem" }}>
                        <Grid narrow>
                          <Column lg={8}>
                            <TimePickerComponent
                              label="Start Time"
                              hour={startHour}
                              minute={startMinute}
                              period={startPeriod}
                              onHourChange={(value) => updateStartTime("hour", value)}
                              onMinuteChange={(value) => updateStartTime("minute", value)}
                              onPeriodChange={(value) => updateStartTime("period", value)}
                              disabled={!isValidPropertyUuid(propertyUuid) || saving || form.availableAllDay}
                            />
                          </Column>
                          <Column sm={4} md={8}lg={8}>
                            <TimePickerComponent
                              label="End Time"
                              hour={endHour}
                              minute={endMinute}
                              period={endPeriod}
                              onHourChange={(value) => updateEndTime("hour", value)}
                              onMinuteChange={(value) => updateEndTime("minute", value)}
                              onPeriodChange={(value) => updateEndTime("period", value)}
                              disabled={!isValidPropertyUuid(propertyUuid) || saving || form.availableAllDay}
                            />
                          </Column>
                        </Grid>
                      </div>
                    )}
                  </FormGroup>

{CommonButtons && (
  <CommonButtons
    onBack={onBack}
    onSave={currentStep === totalSteps ? handleFinish : handleSaveOnly}
    onNext={handleNext}
    showBack
    showSave
    showNext={currentStep < totalSteps}      // hide Next on last step
    saveLabel={currentStep === totalSteps ? "Submit" : "Save"}  // rename label
    isSaveLoading={saving}
    stepData={form}
  />
)}
                </Stack>
              </Form>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
}